import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { supabase, Listing, type ListingType } from "./supabase"
import { getCurrencySymbol } from "../constants/pricing"
import { toast } from "sonner"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function isServiceListing(l: Listing | { type?: string; listing_type?: string; category?: string; is_affiliate?: boolean }): boolean {
  if (l.is_affiliate || (l.type as string) === 'affiliate') return false;
  const rawType = (l.type || (l as any).listing_type || '').toLowerCase();
  const serviceTypes = [
    'service', 'wash_service', 'home_cleaning', 'laundry', 'laundry_service', 
    'mechanic_service', 'barber_service', 'water_service', 'solar_installation', 
    'construction_service', 'professional_service'
  ];
  if (rawType === 'service' || serviceTypes.includes(rawType)) return true;
  if (rawType === 'product' || ['product', 'car_sale', 'spare_part', 'tyres', 'battery', 'solar_product', 'construction_product', 'water_product'].includes(rawType)) return false;
  return ['car_wash', 'cleaning', 'laundry', 'professional_services'].includes(l.category?.toLowerCase() || '');
}

export function isProductListing(l: Listing | { type?: string; listing_type?: string; category?: string; is_affiliate?: boolean }): boolean {
  if (l.is_affiliate || (l.type as string) === 'affiliate') return false;
  return !isServiceListing(l);
}

export function getPlaceholderImage(category?: string): string {
  const categoryPlaceholders: Record<string, string> = {
    car_wash: 'https://images.pexels.com/photos/372810/pexels-photo-372810.jpeg?auto=compress&cs=tinysrgb&w=800',
    auto: 'https://images.pexels.com/photos/190574/pexels-photo-190574.jpeg?auto=compress&cs=tinysrgb&w=800',
    spare_parts: 'https://images.pexels.com/photos/190537/pexels-photo-190537.jpeg?auto=compress&cs=tinysrgb&w=800',
    water: 'https://images.pexels.com/photos/1000084/pexels-photo-1000084.jpeg?auto=compress&cs=tinysrgb&w=800',
    cleaning: 'https://images.pexels.com/photos/4099467/pexels-photo-4099467.jpeg?auto=compress&cs=tinysrgb&w=800',
    laundry: 'https://images.pexels.com/photos/4597284/pexels-photo-4597284.jpeg?auto=compress&cs=tinysrgb&w=800',
    solar: 'https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=800',
    construction: 'https://images.pexels.com/photos/159306/construction-site-build-construction-structure-159306.jpeg?auto=compress&cs=tinysrgb&w=800',
    professional_services: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=800'
  };

  return categoryPlaceholders[category?.toLowerCase() || ''] || 'https://images.pexels.com/photos/443383/pexels-photo-443383.jpeg?auto=compress&cs=tinysrgb&w=800';
}

export function formatCategory(cat: string): string {
  if (!cat) return '';
  const mapping: Record<string, string> = {
    car_wash: 'Car Wash',
    spare_parts: 'Spare Parts',
    professional_services: 'Professional Services',
    auto: 'Automotive'
  };
  
  if (mapping[cat.toLowerCase()]) return mapping[cat.toLowerCase()];
  
  return cat.split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Normalizes any string to clean Title Case / Capitalized letters with smart acronym preservation.
 * Ensures all listing titles across the marketplace and verticals have consistent, premium capital letters.
 */
export function formatListingTitle(title?: string): string {
  if (!title) return 'Untitled Listing';
  const trimmed = title.trim();
  if (!trimmed) return 'Untitled Listing';

  // Acronyms to keep uppercase
  const acronyms = new Set([
    'BMW', 'VW', '4WD', 'AWD', '2WD', 'SUV', 'LED', 'AC', 'A/C', 'V6', 'V8', 'V12',
    'TDI', 'GTI', 'AMG', 'CIPA', 'KVA', 'KW', 'HP', 'HD', 'USB', 'OEM', 'ABS', 'ECU', 
    'HR', 'IT', 'SEO', 'PVR', 'B2B', 'B2C', 'LPG', 'PVC', 'UPS', 'DIY', 'AI'
  ]);

  // Small words to lowercase unless at beginning or after punctuation
  const smallWords = new Set(['and', 'or', 'for', 'with', 'in', 'on', 'at', 'to', 'of', 'a', 'an', 'the', 'by', 'from']);

  const formatWord = (w: string, isFirst: boolean): string => {
    if (!w) return '';
    const upper = w.toUpperCase();
    if (acronyms.has(upper)) return upper;
    const lower = w.toLowerCase();
    if (!isFirst && smallWords.has(lower)) return lower;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  };

  return trimmed
    .split(/\s+/)
    .map((word, idx) => {
      if (word.includes('-')) {
        return word.split('-').map(part => formatWord(part, idx === 0)).join('-');
      }
      if (word.includes('/')) {
        return word.split('/').map(part => formatWord(part, idx === 0)).join('/');
      }
      return formatWord(word, idx === 0);
    })
    .join(' ');
}

export function normalizeListing(l: any): Listing {
  if (!l || typeof l !== 'object') {
    return {
      id: '',
      business_id: '',
      title: '',
      description: '',
      price: 0,
      category: 'other',
      type: 'service',
      images: [],
      created_at: new Date().toISOString()
    } as unknown as Listing;
  }

  // 1. Determine Category (Auto-fallback for legacy)
  let category = (typeof l.category === 'string' ? l.category : '')?.toLowerCase() || 'other';
  const rawType = (l.type || l.listing_type || '').toLowerCase();

  const serviceTypes = [
    'service', 'wash_service', 'home_cleaning', 'laundry', 'laundry_service', 
    'mechanic_service', 'barber_service', 'water_service', 'solar_installation', 
    'construction_service', 'professional_service'
  ];
  const productTypes = [
    'product', 'car_sale', 'spare_part', 'tyres', 'battery', 
    'solar_product', 'construction_product', 'water_product'
  ];

  let typeStr: ListingType = 'service';
  if (l.is_affiliate || rawType === 'affiliate') {
    typeStr = 'affiliate';
  } else if (rawType === 'service' || rawType === 'product') {
    typeStr = rawType as ListingType;
  } else if (serviceTypes.includes(rawType)) {
    typeStr = rawType as ListingType;
  } else if (productTypes.includes(rawType)) {
    typeStr = rawType as ListingType;
  } else if (['car_wash', 'cleaning', 'laundry', 'professional_services'].includes(category)) {
    typeStr = 'service';
  } else {
    typeStr = (rawType as ListingType) || 'service';
  }
  
  // Legacy Mapping
  const washCats = ['wash_service', 'car_wash', 'carwash', 'car wash'];
  const autoCats = ['car_sale', 'spare_part', 'tyres', 'battery', 'mechanic_service', 'wash_service', 'auto', 'car_wash', 'carwash', 'car wash'];
  const homeCats = ['home_cleaning', 'laundry', 'barber_service', 'cleaning', 'laundry_service'];
  const waterCats = ['water_service', 'water_product', 'water'];
  const partsCats = ['spare_part', 'spare_parts'];
  const solarCats = ['solar_installation', 'solar_product', 'solar'];
  const buildCats = ['construction_service', 'construction_product', 'construction', 'contractor'];
  const proCats = ['professional_service', 'consulting', 'legal', 'accounting', 'professional_services'];

  if (autoCats.includes(rawType) || autoCats.includes(l.category)) {
    if (washCats.includes(rawType) || washCats.includes(l.category)) category = 'car_wash';
    else if (partsCats.includes(rawType) || partsCats.includes(l.category)) category = 'spare_parts';
    else category = 'auto';
  } else if (homeCats.includes(rawType) || homeCats.includes(l.category)) {
    if (rawType === 'laundry' || l.category === 'laundry') category = 'laundry';
    else category = 'cleaning';
  } else if (waterCats.includes(rawType) || waterCats.includes(l.category)) {
    category = 'water';
  } else if (solarCats.includes(rawType) || solarCats.includes(l.category)) {
    category = 'solar';
  } else if (buildCats.includes(rawType) || buildCats.includes(l.category)) {
    category = 'construction';
  } else if (proCats.includes(rawType) || proCats.includes(l.category)) {
    category = 'professional_services';
  }

  // 2. Determine Interaction Mode (Unified Frictionless Lead Flow for all products & services)
  const interaction: 'booking' | 'enquiry' = 'enquiry';

  // 3. Normalize Images (CRITICAL FIX for missing marketplace images)
  let images: string[] = [];
  
  // Internal helper to validate image URL
  const isValidImageUrl = (url: any): boolean => {
    return typeof url === 'string' && url.length > 5 && (url.startsWith('http') || url.startsWith('/'));
  };

  // Case A: array of strings or objects
  if (Array.isArray(l.images)) {
    images = l.images.map(img => {
      if (typeof img === 'string') return img;
      if (typeof img === 'object' && img !== null) return (img as any).url || (img as any).uri || (img as any).path || (img as any).image_url;
      return null;
    }).filter(isValidImageUrl);
  } 
  // Case B: stringified JSON array
  else if (l.images && typeof l.images === 'string' && (l.images.startsWith('[') || l.images.startsWith('{'))) {
    try {
      const parsed = JSON.parse(l.images);
      const imgArray = Array.isArray(parsed) ? parsed : [parsed];
      images = imgArray.map(i => {
        if (typeof i === 'string') return i;
        if (typeof i === 'object' && i !== null) return i.url || i.uri || i.path || i.image_url;
        return null;
      }).filter(isValidImageUrl);
    } catch {
      // Not JSON, handle as single string below
    }
  }

  // fallback for other string formats
  if (images.length === 0 && l.images && typeof l.images === 'string') {
    if (l.images.includes(',')) {
      images = l.images.split(',').map(s => s.trim()).filter(isValidImageUrl);
    } else if (l.images.length > 5) {
      images = [l.images];
    }
  }
  
  // Fallback to various potential singular image fields
  if (images.length === 0) {
    const fallbackFields = [l.image_url, l.image, l.feature_image, l.main_image, l.logo_url];
    const validFallback = fallbackFields.find(isValidImageUrl);
    if (validFallback) images = [validFallback];
  }

  // Final fallback pattern - using highly reliable Pexels/Unsplash fallbacks with category awareness
  if (images.length === 0) {
    const categoryPlaceholders: Record<string, string[]> = {
      car_wash: ['https://images.pexels.com/photos/372810/pexels-photo-372810.jpeg?auto=compress&cs=tinysrgb&w=800'],
      auto: ['https://images.pexels.com/photos/190574/pexels-photo-190574.jpeg?auto=compress&cs=tinysrgb&w=800'],
      spare_parts: ['https://images.pexels.com/photos/190537/pexels-photo-190537.jpeg?auto=compress&cs=tinysrgb&w=800'],
      water: ['https://images.pexels.com/photos/1000084/pexels-photo-1000084.jpeg?auto=compress&cs=tinysrgb&w=800'],
      cleaning: ['https://images.pexels.com/photos/4099467/pexels-photo-4099467.jpeg?auto=compress&cs=tinysrgb&w=800'],
      laundry: ['https://images.pexels.com/photos/4597284/pexels-photo-4597284.jpeg?auto=compress&cs=tinysrgb&w=800'],
      solar: ['https://images.pexels.com/photos/356036/pexels-photo-356036.jpeg?auto=compress&cs=tinysrgb&w=800'],
      construction: ['https://images.pexels.com/photos/159306/construction-site-build-construction-structure-159306.jpeg?auto=compress&cs=tinysrgb&w=800'],
      professional_services: ['https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=800'],
      all: [
        'https://images.pexels.com/photos/443383/pexels-photo-443383.jpeg?auto=compress&cs=tinysrgb&w=800',
        'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&w=800'
      ]
    };
    
    const catImages = categoryPlaceholders[category] || categoryPlaceholders['all'];
    const seed = l.id || l.title || 'default';
    const charSum = String(seed).split('').reduce((acc: number, char: string) => acc + char.charCodeAt(0), 0);
    const idx = charSum % catImages.length;
    images = [catImages[idx]];
  }

  // 4. Normalize Pricing Tiers
  let pricing = l.pricing_tiers;
  if (typeof pricing === 'string') {
    try {
      pricing = JSON.parse(pricing);
    } catch {
      pricing = {};
    }
  }

  // 5. Flatten Business Data
  const business = Array.isArray(l.businesses) ? l.businesses[0] : l.businesses;
  const bName = business?.business_name || l.business_name || 'MyServiceHub Partner';
  const bType = business?.business_type || l.business_type || 'registered';
  const vStatus = business?.verification_status || l.verification_status || 'pending_review';
  const badgeType = business?.badge_type || l.badge_type;
  const trustScore = business?.trust_score || l.trust_score || 0;
  const wa = business?.whatsapp_number || l.whatsapp_number;
  const slug = business?.slug || l.business_slug;

  // 6. Parse Affiliate Meta if embedded in description or if type is 'affiliate'
  let isAffiliate = Boolean(l.is_affiliate) || typeStr === 'affiliate';
  let affiliateUrl = l.affiliate_url || '';
  let affiliateButtonText = l.affiliate_button_text || '';
  let cleanDescription = l.description || '';

  if (cleanDescription.includes('[AFFILIATE_META:')) {
    try {
      const match = cleanDescription.match(/\[AFFILIATE_META:([\s\S]*?)\]/);
      if (match && match[1]) {
        const parsed = JSON.parse(match[1]);
        if (parsed.is_affiliate || parsed.url) {
          isAffiliate = true;
          affiliateUrl = parsed.url || affiliateUrl;
          affiliateButtonText = parsed.btn || affiliateButtonText || 'Buy on Partner Site';
        }
      }
      cleanDescription = cleanDescription.replace(/\[AFFILIATE_META:[\s\S]*?\]/g, '').trim();
    } catch (e) {
      console.error('Error parsing affiliate meta tag:', e);
    }
  }

  return {
    ...l,
    title: formatListingTitle(l.title || l.service_name || 'Untitled Listing'),
    description: cleanDescription,
    location: l.location || 'Location upon request',
    category,
    type: typeStr,
    interaction_mode: interaction,
    is_affiliate: isAffiliate,
    affiliate_url: affiliateUrl,
    affiliate_button_text: affiliateButtonText,
    images: images,
    pricing_tiers: pricing,
    business_name: bName,
    business_type: bType,
    verification_status: vStatus,
    badge_type: badgeType,
    trust_score: trustScore,
    whatsapp_number: wa,
    business_slug: slug,
    currency: l.currency,
    business_id: business?.id || l.business_id, // Ensure business_id is exactly the business UUID
    businesses: business // Keep the flattened object for components that need it
  };
}

export function normalizeWhatsApp(phone: string, country: string = 'Botswana'): string {
  if (!phone) return '';
  const trimmed = String(phone).trim();
  let digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  // If already explicitly starts with + and has valid international length (8+ digits)
  if (trimmed.startsWith('+') && digits.length >= 8) {
    return `+${digits}`;
  }

  // Handle international '00' prefix (e.g. 0027... -> +27...)
  if (trimmed.startsWith('00') && digits.length >= 10) {
    return `+${digits.slice(2)}`;
  }

  const prefixes: Record<string, string> = {
    'Botswana': '267',
    'South Africa': '27',
    'Namibia': '264',
    'Zimbabwe': '263',
    'Zambia': '260'
  };

  const prefix = prefixes[country] || '267';

  // Handle leading zero (e.g. 071... -> prefix + 71... or 082... -> 2782...)
  if (digits.startsWith('0')) {
    digits = prefix + digits.slice(1);
  }

  // Handle local 8-digit Botswana numbers without country code
  if (country === 'Botswana' && digits.length === 8 && !digits.startsWith('267')) {
    digits = prefix + digits;
  }

  // Prepend prefix if missing
  if (!digits.startsWith(prefix)) {
    return `+${prefix}${digits}`;
  }

  return `+${digits}`;
}

/**
 * Normalizes and resolves business/merchant logo URLs.
 * Handles full URLs, relative Supabase storage paths, object structures, and public CDN links.
 */
export function resolveLogoUrl(logo: any): string | null {
  if (!logo) return null;
  if (typeof logo === 'object' && logo !== null) {
    logo = (logo as any).url || (logo as any).uri || (logo as any).path || (logo as any).publicUrl || (logo as any).logo_url || '';
  }
  if (typeof logo !== 'string') return null;
  logo = logo.trim();
  if (!logo || logo === 'null' || logo === 'undefined' || logo.length < 3) return null;

  // 1. Direct valid web URLs or data URIs
  if (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('data:image/') || logo.startsWith('blob:')) {
    return logo;
  }
  if (logo.startsWith('//')) {
    return `https:${logo}`;
  }

  // 2. Relative Supabase storage paths (e.g., 'logos/...', 'listing-images/...', 'tenant-logos/...')
  try {
    const cleanPath = logo.replace(/^\/+/, '');
    if (cleanPath.startsWith('listing-images/')) {
      const subPath = cleanPath.replace(/^listing-images\//, '');
      const { data } = supabase.storage.from('listing-images').getPublicUrl(subPath);
      return data.publicUrl;
    }
    if (cleanPath.startsWith('tenant-logos/')) {
      const subPath = cleanPath.replace(/^tenant-logos\//, '');
      const { data } = supabase.storage.from('tenant-logos').getPublicUrl(subPath);
      return data.publicUrl;
    }
    // Default bucket for general uploads
    const { data } = supabase.storage.from('listing-images').getPublicUrl(cleanPath);
    return data.publicUrl;
  } catch {
    return logo;
  }
}

/**
 * Extracts the storage object path for verification documents and generates a short-lived signed URL.
 * Strictly FAILS CLOSED: returns null if generation fails or input is invalid. Never leaks raw storage URLs.
 */
export async function getSecureVerificationDocUrl(rawUrlOrPath: string | null | undefined, expiresInSeconds = 300): Promise<string | null> {
  if (!rawUrlOrPath || typeof rawUrlOrPath !== 'string') return null;
  const trimmed = rawUrlOrPath.trim();
  if (!trimmed) return null;

  try {
    if (trimmed.includes('token=')) {
      // Already an active signed URL
      return trimmed;
    }

    let storagePath = trimmed;
    if (trimmed.includes('/verification-docs/')) {
      const parts = trimmed.split('/verification-docs/');
      if (parts[1]) {
        storagePath = parts[1].split('?')[0];
      }
    } else if (trimmed.startsWith('verification/')) {
      storagePath = trimmed;
    }

    storagePath = storagePath.replace(/^\/+/, '');

    const { data, error } = await supabase.storage
      .from('verification-docs')
      .createSignedUrl(storagePath, expiresInSeconds);

    if (error || !data?.signedUrl) {
      console.warn('[Storage] Failed to generate signed verification doc URL:', error?.message);
      return null;
    }

    return data.signedUrl;
  } catch (err) {
    console.error('[Storage] Exception generating signed verification doc URL:', err);
    return null;
  }
}

/**
 * Safely opens a verification document using a short-lived signed URL.
 * FAILS CLOSED: Never opens or leaks unverified raw URLs.
 */
export async function openSecureVerificationDoc(rawUrlOrPath: string | null | undefined): Promise<void> {
  if (!rawUrlOrPath) return;
  try {
    const signedUrl = await getSecureVerificationDocUrl(rawUrlOrPath, 180);
    if (signedUrl) {
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Unable to securely open this verification document. Please try again.');
    }
  } catch {
    toast.error('Unable to securely open this verification document. Please try again.');
  }
}

/**
 * Opens WhatsApp with a formatted message.
 * Priority: whatsapp:// for mobile apps, wa.me for web fallback.
 */
export function openWhatsApp(phone: string, message: string) {
  const normalizedPhone = normalizeWhatsApp(phone).replace('+', '');
  const encodedText = encodeURIComponent(message);
  
  // Detection logic for mobile
  const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);

  if (isMobile) {
    const mobileUrl = `whatsapp://send?phone=${normalizedPhone}&text=${encodedText}`;
    const webUrl = `https://wa.me/${normalizedPhone}?text=${encodedText}`;
    
    // We try to open the deep link
    window.location.href = mobileUrl;
    
    // Fallback if app not installed (wa.me handles this well generally)
    setTimeout(() => {
      if (document.hasFocus()) {
        window.open(webUrl, '_blank');
      }
    }, 1500);
  } else {
    const webUrl = `https://wa.me/${normalizedPhone}?text=${encodedText}`;
    try {
      const opened = window.open(webUrl, '_blank');
      // If window.open was blocked/prevented by the browser or sandbox, fallback to navigating directly
      if (!opened || opened.closed || typeof opened.closed === 'undefined') {
        window.location.href = webUrl;
      }
    } catch (e) {
      window.location.href = webUrl;
    }
  }
}
